import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { IPaginationData } from '../../../api/base';
import moment from 'moment';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Search } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import theme from '../../../theme';
import faqApi from '../../../api/faq.api';
import { ETrackType, IFaq } from '../../../models/faq.model';
import _ from 'lodash';
import qs from 'query-string';

export default function FaqListPage() {
  const [track, setTrack] = useState(ETrackType.Startup);
  const [dataSource, setDataSource] = useState<IPaginationData<IFaq>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >(['weight', 'asc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [currItem, setCurrItem] = useState<IFaq | undefined>();
  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    faqApi
      .getList({
        ...filter,
        track,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: orderBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [order, orderBy, filter, track]);

  useEffect(loadData, [loadData]);

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">FAQ</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <Link
            to={{
              pathname: `create`,
              search: qs.stringify({ track }),
            }}
          >
            <Button variant="contained" color="success" startIcon={<Add />}>
              Add new
            </Button>
          </Link>
        </div>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          variant="fullWidth"
          value={track}
          onChange={(ev, v) => setTrack(v)}
          aria-label="basic tabs example"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: 18,
            },
          }}
        >
          {Object.values(ETrackType).map((track) => (
            <Tab key={track} label={_.words(track).join(' ')} value={track} />
          ))}
        </Tabs>
      </Box>
      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'weight',
            title: 'Index',
            sortable: true,
            dataKey: 'weight',
            render: (row) => (
              <Button
                onClick={() => setCurrItem(row)}
                color="inherit"
                size="small"
              >
                {row.weight}
              </Button>
            ),
          },
          {
            key: 'question',
            title: 'Question',
            dataKey: 'question_en',
            render: (data) => (
              <>
                <div className="one-lines">{data.question_vi}</div>
                <div className="one-lines">{data.question_en}</div>
              </>
            ),
          },
          {
            key: 'answer',
            title: 'Answer',
            dataKey: 'answer_en',
            render: (data) => {
              const parser = new DOMParser();
              const answerViDoc = parser.parseFromString(
                data.answer_vi,
                'text/html',
              );
              const answerEnDoc = parser.parseFromString(
                data.answer_en,
                'text/html',
              );
              const answer_vi = answerViDoc.body.textContent || '';
              const answer_en = answerEnDoc.body.textContent || '';

              return (
                <>
                  <div className="one-lines">{answer_vi}</div>
                  <div className="one-lines">{answer_en}</div>
                </>
              );
            },
          },

          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            sortable: true,
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            sortable: true,
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            key: 'actions',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <>
                <Link to={data.id}>
                  <IconButton>
                    <Edit />
                  </IconButton>
                </Link>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />
      <Dialog open={!!currItem} onClose={() => setCurrDeletedId(undefined)}>
        <DialogTitle>Update Index</DialogTitle>
        <DialogContent>
          <TextField
            label="Index"
            size="small"
            margin="dense"
            type="number"
            inputProps={{
              min: 1,
            }}
            value={currItem?.weight ?? 1}
            onChange={(e) => {
              if (currItem) {
                setCurrItem({
                  ...currItem,
                  weight: +(e.currentTarget.value || 1),
                });
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrItem(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            variant="contained"
            onClick={() => {
              if (currItem) {
                setDeleting(true);
                faqApi
                  .update(currItem.id, { weight: currItem.weight })
                  .then(() => {
                    loadData();
                    toast.success('Update successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrItem(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="primary"
          >
            {deleting ? <CircularProgress size={16} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this post?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                faqApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete post successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
